/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Button, TextField, ErrorText } from '@components/atoms'
import { Modal } from '@components/molecules'
import { HomeSideNav, Footer } from '@components/systems'

import { useAppSelector, useAppDispatch, customerAuthActions } from '@redux/index'
import { grey } from '@mui/material/colors'
import config from '@configs'
import { HomeNavMenuType } from '@redux/types'
import { produce } from 'immer'

const CustomerChangePasswordPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	const menuList: HomeNavMenuType[] = config.customerMenu()

	// redux status
	const updateCustomerPasswordErrorMessage: string = useAppSelector(
		state => state.customerAuth.updateCustomerPasswordErrorMessage,
	)
	const showCustomerPasswordModal: boolean = useAppSelector(
		state => state.customerAuth.showCustomerPasswordModal,
	)

	// state
	const [passwordInfo, setPasswordInfo] = React.useState({
		prevPassword: '',
		newPassword: '',
		confirmPassword: '',
	})

	const onPrevPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value
		const newData = produce(passwordInfo, draft => {
			draft.prevPassword = str
		})

		setPasswordInfo(newData)
	}

	const onNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value
		const newData = produce(passwordInfo, draft => {
			draft.newPassword = str
		})

		setPasswordInfo(newData)
	}

	const onConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value
		const newData = produce(passwordInfo, draft => {
			draft.confirmPassword = str
		})

		setPasswordInfo(newData)
	}

	const onUpdate = () => {
		reduxDispatch(customerAuthActions.UPDATE_PASSWORD(passwordInfo))
	}

	return (
		<View css={style}>
			<View className="outerView">
				<HomeSideNav menu={menuList} selectedIndes={2} />
				<View className="contentOuterView">
					<Text category="h1">Change Password</Text>

					<View className="contentView">
						<View className="customerInfoView">
							<View className="customerInfoLabelView">
								<Text category="s1">Current Password</Text>
							</View>
							<View className="customerInfoValueView">
								<TextField
									type="password"
									label="Current Password"
									size="small"
									defaultValue={passwordInfo.prevPassword}
									onChange={onPrevPasswordChange}
								/>
							</View>
						</View>

						<View className="customerInfoView">
							<View className="customerInfoLabelView">
								<Text category="s1">New Password</Text>
							</View>
							<View className="customerInfoValueView">
								<TextField
									type="password"
									label="New Password"
									size="small"
									defaultValue={passwordInfo.newPassword}
									onChange={onNewPasswordChange}
								/>
							</View>
						</View>

						<View className="customerInfoView">
							<View className="customerInfoLabelView">
								<Text category="s1">Confirm Password</Text>
							</View>
							<View className="customerInfoValueView">
								<TextField
									type="password"
									label="Confirm Password"
									size="small"
									defaultValue={passwordInfo.confirmPassword}
									onChange={onConfirmPasswordChange}
								/>
							</View>
						</View>

						<View className="bottomView">
							{updateCustomerPasswordErrorMessage !== '' ? (
								<ErrorText className="errorMsg">
									{updateCustomerPasswordErrorMessage}
								</ErrorText>
							) : null}

							<Button className="updateButton" variant="contained" onClick={onUpdate}>
								Update
							</Button>
						</View>
					</View>
				</View>
			</View>

			<Footer />

			<Modal
				target="newGroup"
				title="Password Update Success"
				show={showCustomerPasswordModal}
				onCloseModal={() =>
					reduxDispatch(customerAuthActions.SHOW_CUSTOMER_PASSWORD_MODAL(false))
				}
				closeStatus="inherit"
				showSubmit={false}
				closeTitle="Close"
			>
				<View css={{ width: '30rem' }}>
					<Text category="p2">Your password has been successfully updated.</Text>
				</View>
			</Modal>
		</View>
	)
}

export default CustomerChangePasswordPage

const style = css({
	position: 'relative',
	width: '100%',

	'.outerView': {
		display: 'flex',
		paddingTop: '2rem',
		paddingBottom: '2rem',
		borderBottom: '1px solid',
		borderColor: grey[300],

		'.contentOuterView': {
			width: 'calc(100% - 15rem)',
			minHeight: 'calc(100vh - 10rem)',
			padding: '2rem',
			borderLeft: '1px solid',
			borderColor: grey[300],

			'.contentView': {
				maxWidth: '50rem',
				width: '100%',
				right: 0,
				left: 0,
				margin: 'auto',
				paddingTop: '3rem',
				borderTop: '1px solid',
				borderColor: grey[500],

				'.customerInfoView': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-start',
					height: '4rem',

					'.customerInfoLabelView': {
						width: '10rem',
						marginRight: '2rem',
						border: '1px solid',
					},
					'.customerInfoValueView': {
						width: '38rem',
						textAlign: 'left',
					},
				},

				'.bottomView': {
					marginTop: '5rem',

					'.updateButton': {
						minWidth: '10rem',
						background: grey[900],
					},
				},
			},
		},
	},

	'@media (max-width: 890px)': {
		'.outerView': {
			'.contentOuterView': {
				width: '100%',
				borderLeft: '0px',
			},
		},
	},

	'@media (max-width: 720px)': {
		'.outerView': {
			'.contentOuterView': {
				'.contentView': {
					'.customerInfoView': {
						display: 'block',
						marginBottom: '2rem',

						'.customerInfoLabelView': {
							display: 'none',
						},
						'.customerInfoValueView': {
							width: '100%',
						},
					},
				},
			},
		},
	},
})
